import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import { useFormik } from "formik";
import { DECIMALS_SOL } from "helpers/constans";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Col, Input, Row, Spinner } from "reactstrap";
import { getCampaign, updateAllocationCampaign } from "store/campaign/thunk";
import { getPoolTokenBalance } from "store/wallet/thunk";
import * as Yup from "yup";

export interface Props {
  isOpen?: boolean;
  campaign: any | null;
  listTokenA: any[];
  listTokenB: any[];
  walletCentral: any | null;
  onClose?: () => void;
  triggerRefresh?: (type?: string) => void;
}

const CampaignAllocation = ({ isOpen = false, campaign = null, listTokenA = [], listTokenB = [], walletCentral = null, onClose, triggerRefresh }: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  const LIST_TOKEN_A: any = listTokenA || [];
  const LIST_TOKEN_B: any = listTokenB || [];

  const onCloseClick = () => {
    onClose && onClose();
    formik.resetForm();
  };

  const handleSubmit = async (values: any) => {
    try {
      if (!values?.allocationTokenA && !values?.allocationTokenB) {
        toast(t("Please select at least one allocation type."), CONFIG_OPTION_TOAST_ERROR);
        return;
      }
      setIsLoading(true);
      let allocationTypeBoth: number = 2;
      if (!!values?.allocationTokenA && !!values?.allocationTokenB) {
        allocationTypeBoth = 2;
      }
      if (!!values?.allocationTokenA && !values?.allocationTokenB) {
        allocationTypeBoth = 0;
      }
      if (!values?.allocationTokenA && !!values?.allocationTokenB) {
        allocationTypeBoth = 1;
      }
      const data = {
        id: campaign?.ID,
        allocationType: Number(allocationTypeBoth),
      };
      const response: any = await updateAllocationCampaign(data);
      setIsLoading(false);
      if (response?.data) {
        toast(t("The process has been completed."), CONFIG_OPTION_TOAST_NORMAL);
        triggerRefresh && triggerRefresh();
      } else {
        toast(`${response || "Error - Please try again!"}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading(false);
      toast(`${error?.message || "Error - Please try again!"}`, CONFIG_OPTION_TOAST_ERROR);
    }
  };

  const validationSchema = Yup.object({
    allocationTokenA: Yup.boolean().nullable(),
    allocationTokenB: Yup.boolean().nullable(),
    balance: Yup.object({
      walletCentral: Yup.string().nullable(),
      balanceCentral: Yup.string().nullable(),
      walletA: Yup.string().nullable(),
      walletB: Yup.string().nullable(),
      balanceA: Yup.string().nullable(),
      balanceB: Yup.string().nullable(),
      symbolA: Yup.string().nullable(),
      symbolB: Yup.string().nullable(),
      logoA: Yup.string().nullable(),
      logoB: Yup.string().nullable(),
      symbolCentral: Yup.string().nullable(),
    }).nullable(),
  });

  const formik = useFormik({
    initialValues: {
      allocationTokenA: false,
      allocationTokenB: false,
      balance: {
        walletCentral: '',
        balanceCentral: '',
        walletA: '',
        walletB: '',
        balanceA: '',
        balanceB: '',
        symbolA: '',
        symbolB: '',
        logoA: '',
        logoB: '',
        symbolCentral: 'SOL',
      },
    },
    validationSchema,
    onSubmit: handleSubmit,
  });


  const getCampaignDetail = async (_campaign: any) => {
    setIsLoadingDetail((_prev) => true);
    const params = {
      owner: walletCentral?.pubkey,
      pool: _campaign?.target_pool,
    }
    const [res1, res2]: any = await Promise.all([getCampaign(_campaign?.ID), getPoolTokenBalance(params)]);
    setIsLoadingDetail((_prev) => false);
    if (res1?.data) {
      const data = res1?.data;
      const main_position_config = data?.main_position_config;
      const tokenA = LIST_TOKEN_A?.find((item: any) => item?.value === main_position_config?.token_mint_a) || null;
      const tokenB = LIST_TOKEN_B?.find((item: any) => item?.value === main_position_config?.token_mint_b) || null;
      const dataForm = {
        allocationTokenA: false,
        allocationTokenB: false,
        balance: {
          walletA: res2?.tokens?.tokenA?.address,
          walletB: res2?.tokens?.tokenB?.address,
          walletCentral: walletCentral?.pubkey,
          balanceA: res2?.tokens?.tokenA?.balance?.uiAmountString,
          balanceB: res2?.tokens?.tokenB?.balance?.uiAmountString,
          balanceCentral: `${Number(res2?.native) / Math.pow(10, Number(DECIMALS_SOL))}`,
          symbolA: tokenA?.symbol,
          symbolB: tokenB?.symbol,
          logoA: tokenA?.logo,
          logoB: tokenB?.logo,
          symbolCentral: 'SOL',
        },
      };
      formik.setValues(dataForm);
    }
  };

  useEffect(() => {
    if (!!campaign?.ID) {
      getCampaignDetail(campaign);
    }
  }, [campaign?.ID]);

  return (
    <div className="py-2 relative" style={{ minHeight: "450px" }}>
      {isLoadingDetail && (
        <div className="position-absolute z-index-1000" style={{ zIndex: 998, top: "0px", right: "0px", bottom: "0px", left: "0px", backgroundColor: "#cdcdcd4a", display: "flex", alignItems: "start", justifyContent: "center" }}>
          <span style={{ transform: "translateY(200px)", zIndex: 999 }}><Spinner className="text-primary" /></span>
        </div>
      )}
      <form onSubmit={formik.handleSubmit}>
        {/* Balance Info Section */}
        <div style={{ border: "1px dashed var(--vz-primary)", borderRadius: "4px", padding: "10px" }} className="position-relative py-3 pt-3 mb-4">
          <span className="text-primary position-absolute fs-13 px-1" style={{ backgroundColor: "var(--vz-secondary-bg)", top: "0px", transform: "translateY(-50%)" }}>
            {t("Balance")}
          </span>
          <div className="d-flex flex-column align-items-start gap-1 ms-0">

            <div className="mb-2 pb-2" style={{ borderBottom: "1px dashed #eaeaea" }}>
              <div className="text-muted fs-13">{t('Wallet Central')}: <span className="text-primary fs-13">{formik.values?.balance?.walletCentral || '-'}</span></div>
              <div className="text-muted fs-13">{t('Balance')}: <span className="text-primary fs-13">{formik.values?.balance?.balanceCentral}</span> <span className="text-muted fs-13">{formik.values?.balance?.symbolCentral || ''}</span></div>
            </div>

            <div className="mb-2 pb-2" style={{ borderBottom: "1px dashed #eaeaea" }}>
              <div className="text-muted fs-13">1. {t('Token A')}: <span className="text-primary fs-13">{formik.values?.balance?.walletA || '-'}</span></div>
              <div className="text-muted fs-13">{t('Balance')}: <span className="text-primary fs-13">{formik.values?.balance?.balanceA}</span> <span className="text-muted fs-13">{formik.values?.balance?.symbolA || ''}</span></div>
            </div>

            <div>
              <div className="text-muted fs-13">2. {t('Token B')}: <span className="text-primary fs-13">{formik.values?.balance?.walletB || '-'}</span></div>
              <div className="text-muted fs-13">{t('Balance')}: <span className="text-primary fs-13">{formik.values?.balance?.balanceB}</span> <span className="text-muted fs-13">{formik.values?.balance?.symbolB || ''}</span></div>
            </div>
          </div>
        </div>
        {/* Allocation Info Section */}
        <div style={{ border: "1px dashed var(--vz-primary)", borderRadius: "4px", padding: "10px" }} className="position-relative py-3 pt-4 mb-4">
          <span className="text-primary position-absolute fs-13 px-1" style={{ backgroundColor: "var(--vz-secondary-bg)", top: "0px", transform: "translateY(-50%)" }}>
            {t("Allocation")}
          </span>
          <Row>
            <Col sm={12} md={12} className="align-self-end">
              <div className="mb-4 d-flex align-items-center gap-2">
                <div className="form-check form-switch mb-0">
                  <Input className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="allocation_token_a"
                    name="allocation_token_a"
                    checked={!!formik.values.allocationTokenA}
                    onChange={(event: any) => formik.setFieldValue('allocationTokenA', Boolean(event?.target.checked))}
                  />
                  <label className="form-check-label text-primary fs-13" htmlFor="allocation_token_a">
                    <div className=" d-flex align-items-center">
                      <img src={formik.values?.balance?.logoA} alt={formik.values?.balance?.symbolA} className="w-[18px] h-[18px] rounded-full" />
                      <span className="text-muted font-normal mx-1 fs-14">{formik.values?.balance?.symbolA} -</span>
                      <span className="text-muted font-normal fs-12">
                        {formik.values?.balance?.walletA}
                      </span>
                    </div>
                  </label>
                </div>
                {formik.touched.allocationTokenA && formik.errors.allocationTokenA && (
                  <div className="text-danger fs-12">{formik.errors.allocationTokenA}</div>
                )}
              </div>
            </Col>
            <Col sm={12} md={12} className="align-self-end">
              <div className="mb-2 d-flex align-items-center gap-2">
                <div className="form-check form-switch mb-0">
                  <Input className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="allocation_token_b"
                    name="allocation_token_b"
                    checked={!!formik.values.allocationTokenB}
                    onChange={(event: any) => formik.setFieldValue('allocationTokenB', Boolean(event?.target.checked))}
                  />
                  <label className="form-check-label text-primary fs-13" htmlFor="allocation_token_b">
                    <div className=" d-flex align-items-center">
                      <img src={formik.values?.balance?.logoB} alt={formik.values?.balance?.symbolB} className="w-[18px] h-[18px] rounded-full" />
                      <span className="text-muted font-normal mx-1 fs-14">{formik.values?.balance?.symbolB} -</span>
                      <span className="text-muted font-normal fs-12">
                        {formik.values?.balance?.walletB}
                      </span>
                    </div>
                  </label>
                </div>
                {formik.touched.allocationTokenB && formik.errors.allocationTokenB && (
                  <div className="text-danger fs-12">{formik.errors.allocationTokenB}</div>
                )}
              </div>
            </Col>
          </Row>
        </div>

        {/* Buttons */}
        <Row>
          <Col sm={12} className="mb-2 mt-3">
            <div className="d-flex gap-3 justify-content-end align-items-center">
              <button
                className="btn btn-secondary fs-14 px-1 d-flex justify-content-center align-items-center bg-[#878a99] hover:bg-[#737582]"
                type="button"
                onClick={onCloseClick}
                disabled={isLoading}
                style={{ width: "105px" }}
              >
                <span className="ms-1">{t("Button Close")}</span>
              </button>
              <button
                className="btn btn-primary fs-14 px-1 d-flex justify-content-center align-items-center bg-[#009ef7] hover:bg-[#0086d2]"
                type="submit"
                disabled={isLoading || (!formik.values?.allocationTokenA && !formik.values?.allocationTokenB)}
                style={{ width: "175px" }}
              >
                {isLoading && <Spinner size="sm" className="me-2" />}
                <span>{t("Allocation Campaign")}</span>
              </button>
            </div>
          </Col>
        </Row>
      </form>
    </div>
  );
};

export default CampaignAllocation;